<template>
  <modal
    name="stripe-payment-modal"
    :width="500"
    :clickToClose="!busy"
    @closed="$emit('closed', true)"
  >
    <div class="kt-portlet">
      <div class="close-btn" :style="{'z-index':busy ? 0 : 1}">
        <button
          type="button"
          class="btn btn-light btn-elevate-hover btn-circle btn-icon"
          @click="$modal.hide('stripe-payment-modal')"
        >
          <i class="flaticon2-cross"></i>
        </button>
      </div>
      <div class="kt-portlet__body">
        <div class="credit-card-inputs" :class="{ complete }">
          <div class="row">
            <div class="col-md-12">
              <label for="cardNumber" class="font-weight-bold">
                Card number
              </label>
              <div class="form-group">
                <card-number
                  class="stripe-element card-number form-control"
                  ref="cardNumber"
                  :stripe="site.payment_gateway_token"
                  :options="stripeOptions"
                  @change="updateCardNumber($event)"
                />
                <div class="card-brand">
                  <span v-if="!creditCardIcon" class="input-group-addon">
                    <i class="fab fa-cc-visa mr-1"> </i>
                    <i class="fab fa-cc-mastercard mr-1"> </i>
                    <i class="fab fa-cc-amex mr-1"> </i>
                    <i class="fab fa-cc-discover"> </i>
                  </span>
                  <span v-else class="input-group-addon">
                    <i :class="creditCardIcon"> </i>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-5">
              <div class="form-group">
                <label for="cardExpiry" class="font-weight-bold">
                  Expiration date
                </label>
                <card-expiry
                  class="stripe-element card-expiry form-control"
                  ref="cardExpiry"
                  :stripe="site.payment_gateway_token"
                  :options="stripeOptions"
                  @change="expiry = $event.complete"
                />
              </div>
            </div>
            <div class="col-md-4 pull-right">
              <label for="cardCvc" class="font-weight-bold">
                Security code
              </label>
              <div class="form-group">
                <card-cvc
                  class="stripe-element card-cvc form-control"
                  ref="cardCvc"
                  :stripe="site.payment_gateway_token"
                  :options="stripeOptions"
                  @change="cvc = $event.complete"
                />
                <div class="card-brand">
                  <i
                    class="fa fa-credit-card"
                    :style="{ opacity: cvc ? 1 : 0.5 }"
                  >
                  </i>
                </div>
              </div>
            </div>

            <div class="col-md-3 pull-right">
              <label for="cardCvc" class="font-weight-bold">
                Postal code
              </label>
              <div class="form-group">
                <postal-code
                  class="stripe-element card-postal-code form-control"
                  :class="{ 'is-invalid': hasPostalCodeError }"
                  ref="postalCode"
                  :stripe="site.payment_gateway_token"
                  :options="stripeOptions"
                  @change="updatePostalCode($event)"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="errorMsg"
          class="alert alert-solid-danger alert-bold"
          role="alert"
        >
          <div class="alert-text">{{ errorMsg }}</div>
        </div>

        <button
          class="btn btn-primary btn-block"
          @click="pay"
          :disabled="!complete || busy"
        >
          Pay
          <div v-if="busy" class="spinner-border spinner-border-sm ml-2" role="status">
            <span class="sr-only">Processing...</span>
          </div>
        </button>
        <p class="mt-3">
          <i class="fa fa-lock"></i>
          Payment will be processed securely using Stripe.
        </p>
      </div>
    </div>
  </modal>
</template>

<script>
import {
  CardNumber,
  CardExpiry,
  CardCvc,
  PostalCode,
  confirmPaymentIntent,
} from "vue-stripe-elements-plus";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    CardNumber,
    CardExpiry,
    CardCvc,
    PostalCode,
  },
  data() {
    return {
      complete: false,
      cardNumber: false,
      expiry: false,
      cvc: false,
      postalCode: false,
      creditCardIcon: null,
      stripeOptions: {
        // see https://stripe.com/docs/stripe.js#element-options for details
      },
      error: {},
      busy: false,
    };
  },
  computed: {
    ...mapGetters({
      site: "site/site",
      clientSecret: "order/clientSecret",
    }),
    hasPostalCodeError() {
      return this.error?.code === "incomplete_zip";
    },
    errorMsg() {
      return this.error?.message || null;
    },
  },
  watch: {
    cardNumber() {
      this.updateCard();
    },
    expiry() {
      this.updateCard();
    },
    cvc() {
      this.updateCard();
    },
  },
  methods: {
    ...mapActions({
      setIsCreatingOrder: "order/setIsCreatingOrder",
    }),
    updateCardNumber(event) {
      this.cardNumber = event.complete;

      const cardBrandClass = {
        visa: "fab fa-cc-visa",
        mastercard: "fab fa-cc-mastercard",
        amex: "fab fa-cc-amex",
        discover: "fab fa-cc-discover",
        diners: "fab fa-cc-diners-club",
        jcb: "fab fa-cc-jcb",
        // unknown: "fa fa-credit-card",
        unknown: null,
      };

      this.creditCardIcon =
        cardBrandClass[event.brand] || cardBrandClass["unknown"];
    },
    updatePostalCode(event) {
      this.postalCode = event.complete;

      if (this.hasPostalCodeError) {
        this.error = {};
      }
    },
    updateCard() {
      this.complete = this.cardNumber && this.expiry && this.cvc;

      // field completed, find field to focus next
      if (this.cardNumber) {
        if (!this.expiry) {
          this.$refs.cardExpiry.focus();
          return;
        }
        if (!this.cvc) {
          this.$refs.cardCvc.focus();
          return;
        }
        if (!this.postalCode) {
          this.$refs.postalCode.focus();
          return;
        }
      }

      if (this.expiry) {
        if (!this.cardNumber) {
          this.$refs.cardNumber.focus();
          return;
        }
        if (!this.cvc) {
          this.$refs.cardCvc.focus();
          return;
        }
        if (!this.postalCode) {
          this.$refs.postalCode.focus();
          return;
        }
      }

      if (this.cvc) {
        if (!this.cardNumber) {
          this.$refs.cardNumber.focus();
          return;
        }
        if (!this.expiry) {
          this.$refs.cardExpiry.focus();
          return;
        }
        if (!this.postalCode) {
          this.$refs.postalCode.focus();
          return;
        }
      }
    },

    pay() {
      if(this.busy) {
        return;
      }
      this.busy = true;

      confirmPaymentIntent(this.clientSecret)
        .then((response) => {
          if (response?.error) {
            this.error = response?.error;
            return;
          }

          if (response.paymentIntent.status === "succeeded") {
            this.$modal.hide("stripe-payment-modal");
            this.setIsCreatingOrder(false);

            this.$swal({
              icon: "success",
              title: "Success...",
              text: "Payment process is successfully done!",
            });

            this.$router.push({
              name: "home",
            });
          }
        })
        .catch(() => {
          this.$swal({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong!",
          });
        })
        .finally(() => {
          this.busy = false;
        })
    },
  },
};
</script>

<style scoped>
img.stripe-logo {
  width: 12rem !important;
}
.card-brand {
  font-size: 25px;
  position: absolute;
  right: 16px;
  margin: 0;
  top: 50%;
  transform: translateY(-50%);
}
.close-btn {
  position: absolute;
  right: 0;
  opacity: 0.6;
}
</style>
